/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import styles from './AddBulkUnitsModal.module.css';
import { FloComponentProps } from 'components';
import { FloModal } from 'components';
import { Formik } from 'formik';
import Message from 'containers/common/Message';
import { Form, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FloFormGroup, FloTextArea, FloModalBodyProps } from 'components';
import { Location } from 'model';

interface AddBulkUnitsModalBodyProps extends FloModalBodyProps {
  isSubmitting: boolean,
  parent: Location | null,
  numCreateUnits: number,
  accountId?: string,
  setNumCreateUnits: (numCreateUnits: number) => void,
  createBulkUnits: (unitNames: string[], parent: Location | null) => void,
}

const AddUnitsBulkBody: React.FC<AddBulkUnitsModalBodyProps> = React.memo( ({
  bindSubmitForm,
  bindIsFormValid,
  accountId,
  isSubmitting,
  setNumCreateUnits,
  createBulkUnits,
  parent
}) => {
  const { t } = useTranslation();
  const [unitNames, setUnitNames] = useState([] as string[]);
  const [parentLocation, setParentLocation] = useState(parent);

  useEffect(() => {
    if (parentLocation && accountId) {
      const parent = parentLocation;
      parent.account = { id: accountId }
      setParentLocation(parent);
    }
  }, [parentLocation, accountId])

  // store sanitized state
  const handleUnitNamesChange = (unitNamesInput: string) => {
    const names = unitNamesInput.split('\n').map(s => s.trim()).filter((n) => n.length > 0);

    setUnitNames(names);
    setNumCreateUnits(names.length);
  }

  const formatParentCityStateZip = () => {
    let str = '';
    if (parent?.city) {
      str += parent.city;
    }
    if (parent?.state) {
      str += str.length ? `, ${parent.state}` : parent.state;
    }
    if (parent?.postalCode) {
      str += (str.length ? ' ' : '') + parent.postalCode
    }
    return str;
  }

  const validate = (values: any) => {
    const errors: any = {};
    const names = values.units.split('\n').map((s: string) => s.trim()).filter((n: string) => n.length > 0)
    const duplicateNames: string[] = [];
    names.reduce((map: any, name: string) => {
      if (map[name]) {
        if (map[name] === 1) {
          duplicateNames.push(name);
        }
        return {
          ...map,
          [name]: map[name] + 1
        };
      } else {
        return {
          ...map,
          [name]: 1
        };
      }
    }, {});

    if (duplicateNames.length) {
      errors.units = `${t('add-bulk-units-error-duplicates')} ${duplicateNames.join(', ')}.`
    }
    return errors;
  };
  return (
    <>
      <Message />
      <Formik
        initialValues={{units: ''}}
        onSubmit={(values, actions) => {
          createBulkUnits(unitNames, parentLocation);
        }}
        validate={validate}
        >
          {
            ({touched, errors, handleChange, handleBlur, submitForm, setFieldTouched, isValid, values}) => {
              bindSubmitForm(submitForm);
              bindIsFormValid(isValid);

              const handleTextAreaChange = (e: any) => {
                setFieldTouched('units')
                handleUnitNamesChange(e.target.value);
                handleChange(e);
              }
              return (
                <Form>
                  <Row>
                    <Col>
                      <span>{t('add-bulk-units-instructions')}</span>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <FloFormGroup
                        controlId="units"
                        label = {t('add-bulk-units-input-title')}
                        touched = {touched}
                        errors = {errors}
                        >
                          <FloTextArea
                            name="units"
                            rows={10}
                            placeholder={t('Bulk Units')}
                            onChange={handleTextAreaChange}
                            onBlur={handleBlur}
                            value={values.units}
                            disabled={isSubmitting}
                            isInvalid={!!(touched.units && errors.units)}
                          />
                      </FloFormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <span className={styles.addressInfoHeader}>{t('add-bulk-units-address-label')}</span>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      { parent ? <Row noGutters><span className={styles.addressInfo}>{parent.address}</span></Row> : null }
                      { parent ? <Row noGutters><span className={styles.addressInfo}>{parent.address2}</span></Row> : null }
                      { parent ? <Row noGutters><span className={styles.addressInfo}>{formatParentCityStateZip()}</span></Row> : null}
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <div className={styles.operationWarningContainer}>
                        <span className={styles.operationWarning}><strong>{t('Note')}:</strong> {t('add-bulk-units-long-operation-warning')}</span>
                      </div>
                    </Col>
                  </Row>
                </Form>
              )
            }
          }
        </Formik>
    </>
  );
})

export interface AddBulkUnitsModalProps extends FloComponentProps {
  show:               boolean;
  onHide:             () => void;
  parent:             Location | null;
  createBulkUnits:    (unitNames: string[], parent: Location | null) => void,
  isSubmitting:       boolean;
  accountId?:         string;
}

export const AddBulkUnitsModal: React.FC<AddBulkUnitsModalProps> = React.memo(({
  show,
  parent,
  createBulkUnits,
  isSubmitting,
  accountId,
  ...rest
}) => {
  const { t } = useTranslation();
  const [numCreateUnits, setNumCreateUnits] = useState(0);

  return (

    <FloModal
      {...rest}
      show              = {show}
      name              = "new-location"
      title             = {t('add-bulk-units-title')}
      disableHide       = {isSubmitting}
      isSubmitting      = {isSubmitting}
      disableButtons    = {isSubmitting}
      submitDisplayText = {`${t('Save Units')} (${numCreateUnits})`}
      body              = {(props) => <AddUnitsBulkBody
                                      {...props}
                                      isSubmitting        = {isSubmitting}
                                      parent              = {parent}
                                      numCreateUnits      = {numCreateUnits}
                                      setNumCreateUnits   = {setNumCreateUnits}
                                      createBulkUnits     = {createBulkUnits}
                                      accountId           = {accountId}
                                    />
                                  }
    />
  );
})

export default AddBulkUnitsModal;
